<div class="completed-settlement-page" [class.mobile-view]="isMobileView">

  <div class="settlement-toolbar" [formGroup]="settlementForm">
    <div class="toolbar-section">
      <span class="toolbar-heading">
        <mat-icon>date_range</mat-icon>
        Search settlements
      </span>
      <div class="toolbar-filters">
        <mat-form-field appearance="outline" class="toolbar-field">
          <mat-label>Date from</mat-label>
          <input matInput [matDatepicker]="dateFromPicker" formControlName="StartDate" (click)="dateFromPicker.open()" readonly>
          <mat-datepicker-toggle matSuffix [for]="dateFromPicker"></mat-datepicker-toggle>
          <mat-datepicker #dateFromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="toolbar-field">
          <mat-label>Date to</mat-label>
          <input matInput [matDatepicker]="dateToPicker" [min]="minDate" formControlName="EndDate" (click)="dateToPicker.open()" readonly>
          <mat-datepicker-toggle matSuffix [for]="dateToPicker"></mat-datepicker-toggle>
          <mat-datepicker #dateToPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="toolbar-field">
          <mat-label>Room</mat-label>
          <mat-select formControlName="RoomId">
            <mat-option *ngFor="let room of roomsList" [value]="room.roomId.toString()">
              {{ room.roomName }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <button
          class="search-action"
          type="button"
          (click)="searchSettlements()"
          [disabled]="!settlementForm.valid">
          <mat-icon>search</mat-icon>
          Search
        </button>
      </div>
    </div>

    <div class="toolbar-section filter-section" *ngIf="hasSearched && settledWebinars !== undefined && settledWebinars !== null">
      <span class="toolbar-heading">
        <mat-icon>filter_list</mat-icon>
        Refine results
      </span>
      <mat-form-field appearance="outline" class="toolbar-search">
        <mat-label>Search by transaction, webinar, or file</mat-label>
        <input matInput type="text" (input)="onTextSearch($event)" placeholder="e.g. Test 1234, TWB8..." />
        <mat-icon matPrefix>search</mat-icon>
      </mat-form-field>
    </div>
  </div>

  <div class="results-summary" *ngIf="hasSearched && hasResults()">
    <div class="summary-left">
      <span class="summary-period">{{ getDateRangeLabel() }}</span>
      <span class="summary-room">{{ getSelectedRoomName() }}</span>
    </div>
    <div class="summary-stats">
      <div class="stat-pill">
        <span class="stat-value">{{ filteredSettlements.length }}</span>
        <span class="stat-label">{{ filteredSettlements.length === 1 ? 'Settlement' : 'Settlements' }}</span>
      </div>
      <div class="stat-pill highlight">
        <span class="stat-value">${{ getTotalSettledAmount() | number:'1.2-2' }}</span>
        <span class="stat-label">Total paid</span>
      </div>
    </div>
  </div>

  <div class="empty-state" *ngIf="showEmptyState()">
    <mat-icon>inbox</mat-icon>
    <h3>No settlements found</h3>
    <p>
      No completed settlements for <strong>{{ getSelectedRoomName() }}</strong>
      between {{ getDateRangeLabel() }}.
    </p>
    <p class="empty-hint">Try expanding the date range or selecting a different room.</p>
  </div>

  <div class="settlement-grid" *ngIf="hasResults()">
    <article class="settlement-card" *ngFor="let settlement of filteredSettlements">
      <div class="card-accent"></div>

      <div class="card-inner">
        <header class="card-header">
          <div class="card-identity">
            <h4 class="card-title">{{ settlement.webinar?.name || 'Unnamed webinar' }}</h4>
            <span class="settled-badge">
              <mat-icon>verified</mat-icon>
              Settled
            </span>
          </div>
          <div class="amount-highlight">
            <span class="amount-value">${{ settlement.settledAmount | number:'1.2-2' }}</span>
            <span class="amount-label">Net amount</span>
          </div>
        </header>

        <section class="card-details">
          <div class="detail-row">
            <mat-icon class="detail-icon">receipt</mat-icon>
            <div class="detail-content">
              <span class="detail-label">Transaction no.</span>
              <span class="detail-value">{{ settlement.transactionNumber }}</span>
            </div>
          </div>

          <div class="detail-row">
            <mat-icon class="detail-icon">schedule</mat-icon>
            <div class="detail-content">
              <span class="detail-label">Settled on</span>
              <span class="detail-value">{{ settlement.settledOn | date:'MMM d, yyyy · h:mm a' }}</span>
            </div>
          </div>

          <div class="detail-row" *ngIf="settlement.settledFromBank">
            <mat-icon class="detail-icon">account_balance</mat-icon>
            <div class="detail-content">
              <span class="detail-label">Paid from</span>
              <span class="detail-value">{{ settlement.settledFromBank }}</span>
            </div>
          </div>
        </section>

        <section class="financial-summary">
          <div class="financial-row">
            <span>Gross amount</span>
            <span>${{ getGrossAmount(settlement) | number:'1.2-2' }}</span>
          </div>
          <div class="financial-row deduction">
            <span>Platform charges</span>
            <span>−${{ settlement.platformCharges | number:'1.2-2' }}</span>
          </div>
          <div class="financial-row deduction">
            <span>Shipping charges</span>
            <span>−${{ getShippingCharges(settlement) | number:'1.2-2' }}</span>
          </div>
          <div class="financial-row total">
            <span>Net settlement</span>
            <span>${{ settlement.settledAmount | number:'1.2-2' }}</span>
          </div>
        </section>

        <footer
          class="proof-footer"
          *ngIf="settlement.image"
          role="button"
          tabindex="0"
          (click)="onOpenSettlementProof(settlement.image)"
          (keydown.enter)="onOpenSettlementProof(settlement.image)"
          (keydown.space)="onOpenSettlementProof(settlement.image); $event.preventDefault()"
          [attr.aria-label]="'View settlement proof for ' + settlement.transactionNumber"
          [title]="getProofFileName(settlement.filePath)">
          <div class="proof-icon-wrap">
            <mat-icon>image</mat-icon>
          </div>
          <div class="proof-copy">
            <span class="proof-action">View payment proof</span>
            <span class="proof-file">{{ getShortProofFileName(settlement.filePath) }}</span>
          </div>
          <mat-icon class="proof-chevron">chevron_right</mat-icon>
        </footer>

        <p class="no-proof" *ngIf="!settlement.image">
          <mat-icon>info_outline</mat-icon>
          No payment proof uploaded
        </p>
      </div>
    </article>
  </div>
</div>
